import * as React from 'react';
import { Box, Chip, IconButton, styled, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { useDownloadAsset } from 'lib/hooks/useDownloadAsset';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { SettingDisplay } from 'lib/SettingDisplay';
import { Option } from 'lib/types';

const retrievalByOptions: Option[] = [
    { label: 'Table Number', value: 'TABLE_NUMBER' },
    { label: 'Check Number', value: 'CHECK_NUMBER' },
    { label: 'Both', value: 'BOTH' }
];

const PREFIX = 'PayAtTableSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    image: `${PREFIX}-image`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.image}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '400px',
        maxWidth: '100%'
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const PayAtTableSettings = ({ settings, onSubmit, tenantId }: IProps) => {
    const retrievalByLabel = React.useMemo(
        () =>
            settings
                ? retrievalByOptions.find(option => option.value === settings.payAtTable?.retrievalBy)
                      ?.label ?? 'None'
                : 'None',
        [settings]
    );

    const { loading: webMenuSelectionImageLoading, handleDownload: handleDownloadWebMenuSelectionImage } =
        useDownloadAsset({
            src: `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/web_menu_selection`,
            fileName: 'web_menu_selection'
        });

    return (
        <StyledMuiCardForm
            title="Pay at Table"
            initialValues={{
                noTableCheckFoundUrl: settings.payAtTable?.noTableCheckFoundUrl,
                retrievalBy: settings.payAtTable?.retrievalBy
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay
                        label="No Check Found URL"
                        description="The URL shown to the user, when no check is found at their table"
                    >
                        <Typography>{settings.payAtTable?.noTableCheckFoundUrl ?? 'None'}</Typography>
                    </SettingDisplay>
                    <SettingDisplay
                        label="Order Retrieval By"
                        description="Retrieve the order using which of the following"
                    >
                        <Chip color="primary" label={retrievalByLabel} />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Web Menu Image"
                        description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                    >
                        {/* TODO: replace with SettingImageDisplay or something like that */}
                        <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={1}>
                            <img
                                src={`${
                                    import.meta.env.MEDIA_SERVICE_URL
                                }/tenants/${tenantId}/app_assets/web_menu_selection?t=${new Date().toTimeString()}`}
                                className={classes.image}
                            />
                            <IconButton
                                disabled={webMenuSelectionImageLoading}
                                onClick={handleDownloadWebMenuSelectionImage}
                            >
                                <Download />
                            </IconButton>
                        </Box>
                    </SettingDisplay>
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay
                        label="No Check Found URL"
                        description="The URL shown to the user, when no check is found at their table"
                    >
                        <TextFormField name="noTableCheckFoundUrl" />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Order Retrieval By"
                        description="Retrieve the order using which of the following"
                    >
                        <SelectFormField options={retrievalByOptions} name="retrievalBy" />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Web Menu Image"
                        description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                    >
                        <ImageFormField
                            name="menuCardImage"
                            aspect={657 / 220}
                            fileName="web_menu_selection"
                            uploadFolder="app_assets"
                        />
                    </SettingDisplay>
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
